export const state = {
    notifications: [],
    unreadCount: 0,
    loading: false,
    lastFetch: null,
    initialized: false,
};

export const getters = {
    unreadNotifications: (state) => {
      return state.notifications.filter(notification => !notification.read_at);
    },
    highPriorityNotifications: (state) => {
      return state.notifications.filter(notification => notification.priority >= 3);
    },
    hasUnread: (state) => {
      return state.unreadCount > 0;
    },
};
export const mutations = {
    SET_NOTIFICATIONS(state, notifications) {
      state.notifications = notifications;
    },
    SET_UNREAD_COUNT(state, count) {
      state.unreadCount = count;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_LAST_FETCH(state, timestamp) {
      state.lastFetch = timestamp;
    },
    ADD_NOTIFICATION(state, notification) {
      state.notifications.unshift(notification);
      if (!notification.read_at) {
        state.unreadCount++;
      }
    },
    UPDATE_NOTIFICATION(state, updatedNotification) {
      const index = state.notifications.findIndex(n => n.id === updatedNotification.id);
      if (index !== -1) {
        // If notification was marked as read, decrease unread count
        if (!state.notifications[index].read_at && updatedNotification.read_at) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        state.notifications.splice(index, 1, updatedNotification);
      }
    },
    REMOVE_NOTIFICATION(state, notificationId) {
      const index = state.notifications.findIndex(n => n.id === notificationId);
      if (index !== -1) {
        const notification = state.notifications[index];
        if (!notification.read_at) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        state.notifications.splice(index, 1);
      }
    },
    MARK_ALL_AS_READ(state) {
      state.notifications.forEach(notification => {
        if (!notification.read_at) {
          notification.read_at = new Date().toISOString();
        }
      });
      state.unreadCount = 0;
    },
    SET_INITIALIZED(state, initialized) {
      state.initialized = initialized;
    },
  };

export const actions = {
    // Initialize notifications after login
    async initializeNotifications({ commit, state }) {

      if (state.initialized) return;

      commit('SET_LOADING', true);
      commit('SET_INITIALIZED', true);

      try {
        const Notification = (await import('@models/Notification')).default;
        const response = await Notification.unread();

        commit('SET_NOTIFICATIONS', response.data);
        commit('SET_UNREAD_COUNT', response.total);
        commit('SET_LAST_FETCH', Date.now());
      } catch (error) {
        console.error('Failed to initialize notifications:', error);
        commit('SET_INITIALIZED', false);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Reset notifications on logout
    resetNotifications({ commit }) {
      commit('SET_NOTIFICATIONS', []);
      commit('SET_UNREAD_COUNT', 0);
      commit('SET_INITIALIZED', false);
      commit('SET_LAST_FETCH', null);
    },

    // Fetch all notifications (manual refresh)
    // Use this for initialize for now
    async fetchNotifications({ commit }) {
      commit('SET_LOADING', true);

      try {
        const Notification = (await import('@models/Notification')).default;
        const response = await Notification.get();

        commit('SET_NOTIFICATIONS', response.data);
        commit('SET_LAST_FETCH', Date.now());
        commit('SET_INITIALIZED', true);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Fetch unread notifications (manual refresh)
    async fetchUnreadNotifications({ commit }) {
      try {
        const Notification = (await import('@models/Notification')).default;
        const response = await Notification.unread();

        commit('SET_NOTIFICATIONS', response.data);
        commit('SET_UNREAD_COUNT', response.total);
        commit('SET_LAST_FETCH', Date.now());
      } catch (error) {
        console.error('Failed to fetch unread notifications:', error);
      }
    },

    // Mark notification as read
    async markAsRead({ commit }, notificationId) {
      try {
        const Notification = (await import('@models/Notification')).default;
        const notification = new Notification();
        notification.id = notificationId;

        const response = await notification.markAsRead();
        commit('UPDATE_NOTIFICATION', response.data.notification);

      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    },

    // Mark all notifications as read
    async markAllAsRead({ commit }) {
      try {
        const Notification = (await import('@models/Notification')).default;
        await Notification.markAllAsRead();

        commit('MARK_ALL_AS_READ');
      } catch (error) {
        console.error('Failed to mark all notifications as read:', error);
      }
    },

    // Dismiss notification
    async dismissNotification({ commit }, notificationId) {
      try {
        const Notification = (await import('@models/Notification')).default;
        const notification = new Notification();
        notification.id = notificationId;

        await notification.dismiss();

        commit('REMOVE_NOTIFICATION', notification.id);

      } catch (error) {
        console.error('Failed to dismiss notification:', error);
      }
    },

    // Snooze notification
    async snoozeNotification({ commit }, { notificationId, duration = 'P1D' }) {
      try {
        const Notification = (await import('@models/Notification')).default;
        const notification = new Notification();
        notification.id = notificationId;

        await notification.snooze(duration);

        // Remove from current notifications (it will reappear when snooze expires)
        commit('REMOVE_NOTIFICATION', notificationId);

      } catch (error) {
        console.error('Failed to snooze notification:', error);
      }
    },
  };
